/* =====================================================================
   THEME RUNTIME — CUSTOMER PORTAL (Jose 2026-09-08, Phase 3).
   Reads the owner's theme from the PUBLIC mirror publicConfig/theme (settings/shop
   is staff-only; the staff app mirrors on every theme save — mirrorPublicTheme).
   Applies the brand color via branding.applyTheme + exposes the per-element tones
   (portalHeroes/portalButtons/portalCards) so customer surfaces reflect the shop's look.
   Mirrors the staff src/theme/themeRuntime.ts contract.
===================================================================== */
import { useSyncExternalStore } from 'react';
import type { CSSProperties } from 'react';
import { doc, getDoc } from 'firebase/firestore';
import { db } from '../config/firebase';
import { applyTheme, SLIK_THEME } from './branding';

export type ThemeTone = 'light' | 'medium' | 'dark';
export interface ThemeConfig {
  brand: { primary: string; hero: string; accent: string };
  tones: Record<string, ThemeTone>;
  intensity: number;
}

export const DEFAULT_THEME: ThemeConfig = {
  brand: { primary: '#C0824A', hero: '#6f4a30', accent: '#2f4a7a' },
  tones: {
    portalHeroes: 'medium', portalButtons: 'light', portalCards: 'dark',
  },
  intensity: 0.6,
};

let current: ThemeConfig = DEFAULT_THEME;
const listeners = new Set<() => void>();

export function getTheme(): ThemeConfig { return current; }
export function setTheme(next: Partial<ThemeConfig>): void {
  current = {
    ...DEFAULT_THEME, ...next,
    brand: { ...DEFAULT_THEME.brand, ...(next.brand || {}) },
    tones: { ...DEFAULT_THEME.tones, ...(next.tones || {}) },
    intensity: next.intensity ?? DEFAULT_THEME.intensity,
  };
  listeners.forEach((l) => l());
}
function subscribe(fn: () => void): () => void { listeners.add(fn); return () => { listeners.delete(fn); }; }
export function useTheme(): ThemeConfig { return useSyncExternalStore(subscribe, getTheme, getTheme); }
export function toneFor(key: string): ThemeTone { return current.tones[key] || 'medium'; }

const hexToRgb = (h: string): [number, number, number] => {
  let s = h.replace('#', ''); if (s.length === 3) s = s.split('').map((c) => c + c).join('');
  const n = parseInt(s, 16); return [(n >> 16) & 255, (n >> 8) & 255, n & 255];
};
const toHex = (r: number, g: number, b: number) =>
  '#' + [r, g, b].map((x) => Math.max(0, Math.min(255, Math.round(x))).toString(16).padStart(2, '0')).join('');
const dk = (h: string, a: number) => { const [r, g, b] = hexToRgb(h); return toHex(r * (1 - a), g * (1 - a), b * (1 - a)); };
const lt = (h: string, a: number) => { const [r, g, b] = hexToRgb(h); return toHex(r + (255 - r) * a, g + (255 - g) * a, b + (255 - b) * a); };
const shadow = (k: number) => `0 ${6 + 8 * k}px ${16 + 14 * k}px -12px rgba(0,0,0,${0.35 + 0.25 * k}), inset 0 1px 0 rgba(255,255,255,0.07)`;

export function matteTone(hex: string, tone: ThemeTone = 'medium', intensity: number = current.intensity): CSSProperties {
  const span = 0.24 + 0.30 * intensity; const sh = shadow(intensity);
  if (tone === 'dark') return { background: `linear-gradient(140deg, ${dk(hex, 0.08)} 0%, ${dk(hex, 0.08 + span)} 100%)`, color: '#fff', boxShadow: sh, border: '1px solid rgba(255,255,255,0.10)' };
  if (tone === 'medium') return { background: `linear-gradient(140deg, ${lt(hex, 0.16)} 0%, ${dk(hex, span * 0.6)} 100%)`, color: '#fff', boxShadow: sh, border: '1px solid rgba(255,255,255,0.10)' };
  return { background: `linear-gradient(140deg, ${lt(hex, 0.46)} 0%, ${lt(hex, 0.06)} 100%)`, color: '#2a1f16', boxShadow: sh, border: '1px solid rgba(0,0,0,0.06)' };
}
export function elementStyle(elementKey: string, hex: string): CSSProperties {
  return matteTone(hex, toneFor(elementKey), current.intensity);
}

/** Boot: read the public theme mirror, set tones + retint the brand. Non-fatal. */
export async function loadPublicTheme(): Promise<void> {
  try {
    const snap = await getDoc(doc(db, 'publicConfig', 'theme'));
    const t = snap.exists() ? (snap.data() as any).theme : null;
    if (t) {
      setTheme(t);
      applyTheme({ ...SLIK_THEME, primary: t.brand?.primary || SLIK_THEME.primary });
    }
  } catch { /* keep defaults */ }
}
